import { Box, Textarea, SimpleGrid, Text, Flex, Button, FileButton, Image, Modal, MultiSelect } from "@mantine/core";
import { IconPlus, IconX } from "@tabler/icons-react";
import { getFileUrls } from "@/shared/utils/useFileUrl";
import { useState, useEffect, useRef, useMemo } from "react";
import { errorNotification } from "@/shared/utils/errorNotification";
import {
  getSubIndustriesByIndustry,
  getCollaborationIntentOptions,
  getWhoAmIOptions,
} from "@/components/shared/constants/general.const";

interface BusinessOverviewProps {
  formData: {
    companyDescription: string;
    industryCategory: string[];
    subIndustry: string[];
    whoAmI: string[];
    collaborationIntent: string[];
  };
  updateForm: (field: string, value: any) => void;
  industryOptions: { value: string; label: string }[];
  companyImages: File[];
  existingImages?: any[];
  handleImageUpload: (files: File[]) => void;
  removeImage: (index: number) => void;
  removeExistingImage?: (index: number) => void;
  aiImages?: string[];
  removeAiImage?: (index: number) => void;
  disabled?: boolean;
}

export default function BusinessOverview({
  formData,
  updateForm,
  industryOptions,
  companyImages,
  existingImages = [],
  handleImageUpload,
  removeImage,
  removeExistingImage,
  aiImages = [],
  removeAiImage,
  disabled,
}: BusinessOverviewProps) {
  const fileInputRef = useRef<() => void>(null);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Get sub-industry options based on all selected industries (merged + deduplicated)
  const subIndustryOptions =
    formData.industryCategory.length > 0
      ? formData.industryCategory
          .flatMap((ind) => getSubIndustriesByIndustry(ind))
          .filter((opt, i, arr) => arr.findIndex((o) => o.value === opt.value) === i)
      : [];

  // Memoize fileIds to prevent unnecessary effect runs
  const fileIds = useMemo(() => existingImages.map((img) => img.id), [existingImages.length]);

  useEffect(() => {
    let mounted = true;

    const loadPreviews = async () => {
      // Fetch presigned URLs for existing images using batch API
      let existingPreviews: string[] = [];
      if (fileIds.length > 0) {
        try {
          const urlMap = await getFileUrls(fileIds);
          existingPreviews = fileIds.map((id) => urlMap[id] || "").filter(Boolean);
        } catch (err) {
          console.error("Failed to load image previews:", err);
        }
      }

      // AI Images are already URLs
      const aiPreviews = aiImages;

      // Create blob URLs for new uploads
      const newPreviews = companyImages.map((file) => URL.createObjectURL(file));

      if (mounted) {
        setImagePreviews([...existingPreviews, ...aiPreviews, ...newPreviews]);
      }

      // Cleanup function for blob URLs
      return () => {
        newPreviews.forEach((url) => URL.revokeObjectURL(url));
      };
    };

    loadPreviews();

    return () => {
      mounted = false;
    };
  }, [fileIds.join(","), companyImages.length, aiImages.length]);

  // Clear sub-industry when industry changes
  const handleIndustryChange = (values: string[]) => {
    updateForm("industryCategory", values);
    updateForm("subIndustry", []); // Reset sub-industries when industry changes
  };

  return (
    <Box>
      {/* Image Preview Modal */}
      <Modal opened={!!previewImage} onClose={() => setPreviewImage(null)} size="xl" title="Image Preview" centered>
        {previewImage && <Image src={previewImage} alt="Preview" radius="md" />}
      </Modal>

      {/* Company Description */}
      <Box mb="md">
        <Text fz="sm" fw={500} mb={4}>
          Company Description
          <Text span c="red" fz="lg">
            {" "}
            *
          </Text>
        </Text>
        <Textarea
          placeholder="Describe your company, what you do, your expertise, and what makes you unique..."
          autosize
          minRows={5}
          maxLength={800}
          value={formData.companyDescription}
          onChange={(e) => updateForm("companyDescription", e.currentTarget.value)}
          disabled={disabled}
          error={formData.companyDescription.length > 800 ? "Description cannot exceed 800 characters" : null}
        />
        <Flex justify="space-between" mt={4}>
          <Text fz="xs" c="slate.6">
            Describe your company, expertise, and what makes you unique.
          </Text>
          <Text fz="xs" c="orange">
            {formData.companyDescription.length} / 800 characters
          </Text>
        </Flex>
      </Box>

      {/* Industry Category & Sub-industry */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="xl">
        <MultiSelect
          label={
            <Text fz="sm" fw={500}>
              Industry Category
              <Text span c="red" fz="lg">
                {" "}
                *
              </Text>
            </Text>
          }
          placeholder="Select industries"
          data={industryOptions}
          value={formData.industryCategory || []}
          onChange={handleIndustryChange}
          searchable
          disabled={disabled}
          clearable
        />
        <MultiSelect
          label={
            <Text fz="sm" fw={500}>
              Sub-industry (Optional)
            </Text>
          }
          placeholder={formData.industryCategory.length > 0 ? "Select sub-industries" : "Select industry first"}
          data={subIndustryOptions}
          value={formData.subIndustry || []}
          onChange={(value) => updateForm("subIndustry", value)}
          searchable
          disabled={disabled || formData.industryCategory.length === 0}
          clearable
          maxValues={5}
        />
      </SimpleGrid>

      {/* Who Am I */}
      <Box mb="md">
        <MultiSelect
          label={
            <Text fz="sm" fw={500}>
              Who am I?
              <Text span c="red" fz="lg">
                {" "}
                *
              </Text>
            </Text>
          }
          description="Identify your role in the industry"
          placeholder="Select your role"
          data={getWhoAmIOptions()}
          value={formData.whoAmI || []}
          onChange={(value) => updateForm("whoAmI", value)}
          searchable
          disabled={disabled}
          clearable
        />
      </Box>

      {/* Collaboration Intent */}
      <Box mb="xl">
        <MultiSelect
          label={
            <Text fz="sm" fw={500}>
              Collaboration Intent (Optional)
            </Text>
          }
          description="Select the types of collaboration you're looking for"
          placeholder="Select collaboration types"
          data={getCollaborationIntentOptions()}
          value={formData.collaborationIntent || []}
          onChange={(value) => updateForm("collaborationIntent", value)}
          searchable
          disabled={disabled}
          clearable
          maxValues={5}
        />
      </Box>

      {/* Company Images */}
      <Text fz="sm" fw={500} mb={4}>
        Company Images (Optional)
      </Text>
      <Text fz="xs" c="slate.6" mb="sm">
        Upload photos of your facilities, products, or team (max 6 images)
      </Text>
      <Flex gap="sm" wrap="wrap" mb="sm">
        {imagePreviews.map((preview, index) => {
          // Calculate thresholds for different image types
          const existingCount = existingImages.length;
          const aiCount = aiImages.length;

          const isExisting = index < existingCount;
          const isAi = index >= existingCount && index < existingCount + aiCount;
          const isNew = index >= existingCount + aiCount;

          return (
            <Box key={index} pos="relative" style={{ width: 80, height: 80 }}>
              <Image
                src={preview}
                alt={`Company image ${index + 1}`}
                radius="lg"
                fit="cover"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  border: "1px solid #E5E7EB",
                  cursor: "pointer",
                }}
                onClick={() => setPreviewImage(preview)}
                onError={(e) => {
                  console.error("❌ Image failed to load:", preview);
                  e.currentTarget.style.display = "none";
                }}
              />
              {!disabled && (
                <Button
                  size="xs"
                  color="red"
                  variant="filled"
                  p={4}
                  pos="absolute"
                  top={-8}
                  right={-8}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isNew) {
                      // Adjust index to be relative to companyImages array
                      removeImage(index - existingCount - aiCount);
                    } else if (isAi && removeAiImage) {
                      // Adjust index to be relative to aiImages array
                      removeAiImage(index - existingCount);
                    } else if (isExisting && removeExistingImage) {
                      removeExistingImage(index);
                    }
                  }}
                  style={{
                    borderRadius: "50%",
                    minWidth: "auto",
                    width: 24,
                    height: 24,
                    padding: 0,
                  }}
                >
                  <IconX size={12} />
                </Button>
              )}
            </Box>
          );
        })}

        {!disabled && imagePreviews.length < 6 && (
          <FileButton
            resetRef={fileInputRef}
            onChange={(files) => {
              if (files && files.length > 0) {
                const maxSize = 5 * 1024 * 1024; // 5MB
                const invalidFiles = files.filter((file) => file.size > maxSize);

                if (invalidFiles.length > 0) {
                  errorNotification({
                    title: "File too large",
                    message: `${invalidFiles.length} image(s) exceed the 5MB limit`,
                  });
                  fileInputRef.current?.();
                  return;
                }

                const remainingSlots = 6 - imagePreviews.length;
                if (files.length > remainingSlots) {
                  errorNotification({
                    title: "Too many images",
                    message: `You can only add ${remainingSlots} more image(s)`,
                  });
                  fileInputRef.current?.();
                  return;
                }

                handleImageUpload(files);
                fileInputRef.current?.();
              }
            }}
            accept="image/png,image/jpeg"
            multiple
          >
            {(props) => (
              <Box
                {...props}
                style={{
                  width: 80,
                  height: 80,
                  border: "none",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  backgroundColor: "#DBEAFE",
                }}
              >
                <IconPlus size={24} color="#3B82F6" />
              </Box>
            )}
          </FileButton>
        )}
      </Flex>
      <Text fz="xs" c="slate.6">
        PNG, JPG up to 5MB each. These images will be visible in your public profile.
      </Text>
    </Box>
  );
}
